import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ContractActionConstraintDto } from '../../beneficiaries/beneficiary-folder/contract/contract-action.dto';
import { DatesNewContractDto } from '../../beneficiaries/beneficiary-folder/contract/contract-details/dates-new-contract-dto';
import { ContractDto } from '../../beneficiaries/beneficiary-folder/contract/contract.dto';
import { DisplayContract } from '../../beneficiaries/beneficiary-folder/contract/displayContract';
import { environment } from '../../environments/environment';
import { Pagination } from '../pagination/pagination';
import { PaginationOptions } from '../pagination/pagination-options';
import { TraceabilityDto } from '../traceability/traceability.dto';
import { ApiService } from './api.service';
import { FileService } from './file.service';

@Injectable({
  providedIn: 'root',
})
export class ContractsService {
  public contractUpdate: Subject<ContractDto> = new Subject<ContractDto>();
  public contractUpdated$: Observable<ContractDto> = this.contractUpdate.asObservable();
  constructor(
    private readonly api: ApiService,
    private readonly fileService: FileService,
  ) {}

  public async findByBeneficiaryId(id: number, options: PaginationOptions): Promise<Pagination<DisplayContract>> {
    const uri: string = environment.api.contracts + '/' + id;
    return this.api.getPaginated<DisplayContract>(options.buildUrl(uri));
  }

  public async findByContractId(contractId: number): Promise<ContractDto> {
    const result = await this.api.get<ContractDto>(environment.api.contracts + '/' + contractId + '/details');
    const dto: ContractDto = ContractDto.fromJson(result);
    return dto;
  }

  public async saveContract(contract: ContractDto): Promise<void> {
    await this.api.post(environment.api.contracts, contract);
    this.contractUpdate.next(contract);
  }

  public async saveContractActions(contract: ContractDto): Promise<void> {
    await this.api.post(environment.api.contracts + '/save-actions', contract);
    this.contractUpdate.next(contract);
  }

  public async delete(id: number): Promise<void> {
    await this.api.delete(environment.api.contracts + '/' + id);
    this.contractUpdate.next(undefined);
  }

  public async updateStatus(traceabilityDto: TraceabilityDto, contractType: string): Promise<void> {
    await this.api.post(environment.api.contracts + '/status/' + contractType, traceabilityDto);
    this.contractUpdate.next(undefined);
  }

  public async getDatesContract(beneficiaryId: number): Promise<DatesNewContractDto> {
    const result = await this.api.get<DatesNewContractDto>(environment.api.contracts + '/' + beneficiaryId + '/get-new-contracts-dates');
    return {
      beginDateContractSocioPro: new Date(result.beginDateContractSocioPro),
      endDateContractSocioPro: new Date(result.endDateContractSocioPro),
      beginDateContractIER: new Date(result.beginDateContractIER),
      endDateContractIER: new Date(result.endDateContractIER),
      beginDateContractClassic: new Date(result.beginDateContractClassic),
      endDateContractClassic: new Date(result.endDateContractClassic),
      beginDateContractEngagement: new Date(result.beginDateContractEngagement),
      minBeginDateContractEngagement: result.minBeginDateContractEngagement ? new Date(result.minBeginDateContractEngagement) : null,
      contractStillActive: result.contractStillActive,
    };
  }

  public async findAllConstraints(): Promise<ContractActionConstraintDto[]> {
    const uri: string = environment.api.contracts + '/constraints';
    return this.api.get<ContractActionConstraintDto[]>(uri);
  }

  public async findPrevious(idBeneficiary: number, idContract?: number): Promise<ContractDto | null> {
    let uri: string = `${environment.api.contracts}/${idBeneficiary}/previous`;
    if (idContract) uri += `?contractId=${idContract}`;
    return this.api.get<ContractDto | null>(uri);
  }

  public async generateContratEngagementDocument(idContract: number) {
    const uri: string = environment.api.contracts + '/' + idContract + '/generate-contract';
    const data: Blob = await this.api.getFile(uri);
    return this.fileService.saveAs(data, "Contrat d'engagement.pdf");
  }
}
